import {
  ButtonGroup,
  Card,
  DataStatus,
  Form,
  FormButton,
  FormSubmitHandler,
  openDialog,
  Separator,
  Tooltip,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import {useUpdateNoteMutation} from '@dms/api/core';
import {
  useChangeOrderRentVehicleMutation,
  useCreateRentalOrderContractDocumentMutation,
  useGetRentalOrderQuery,
  useRentingStateQuery,
  useRentRentalOrderVehicleMutation,
} from '@dms/api/rental';
import {useGetVehicleMileageQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetRentalVehicleQuery, useGetVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  handleApiError,
  levelIndicatorsFor,
  useUpdateOdometer,
  useVatRatesOptions,
} from '@dms/shared';

import {ApiError, Nullish, useRequiredParams} from 'shared/web';

import {RentalOrderNodeContext} from '../../../../constants/RentalOrderNodeContext';
import {AuthorizedPersons} from './components/AuthorizedPersons';
import {ConditionWhenRenting} from './components/ConditionWhenRenting';
import {NotRentableVehicleAlert} from './components/NotRentableVehicleAlert';
import {Pricing} from './components/Pricing';
import {RentalNote} from './components/RentalNote';
import {RentAndReturnAddress} from './components/RentAndReturnAddress';
import {SelectTemplateAndSubmit} from './components/SelectTemplateAndSubmit';
import {useGetRentVehicleFormDefaultValues} from './hooks/useGetRentVehicleFormDefaultValues';
import {rentVehicleFormSchema, RentVehicleFormValues} from './types/RentVehicleFormValues';
import {getRentVehicleRequestBody} from './utils/getRentVehicleRequestBody';

export function RentVehicleForm() {
  const {id: rentalOrderId} = useRequiredParams();

  const {
    data: rentalOrder,
    isLoading: isLoadingRentalOrder,
    isError: isErrorRentalOrder,
  } = useGetRentalOrderQuery({rentalOrderId});

  const {data: vehicleMileage} = useGetVehicleMileageQuery(
    {vehicleId: rentalOrder?.rentalVehicleId ?? ''},
    {skip: isNil(rentalOrder?.rentalVehicleId)}
  );

  const {data: rentingState} = useRentingStateQuery(
    {rentalOrderId},
    {refetchOnMountOrArgChange: true}
  );

  const isVehicleNotRentable =
    rentingState?.isRentable === false && rentalOrder?.state === 'reserved';

  const [rentVehicle] = useRentRentalOrderVehicleMutation();
  const [updateRentalOrderVehicle] = useChangeOrderRentVehicleMutation();
  const [generateRentalContract] = useCreateRentalOrderContractDocumentMutation();
  const [addNote] = useUpdateNoteMutation();
  const [vatRates] = useVatRatesOptions();

  const {
    data: rentalVehicle,
    isLoading: isLoadingRentalVehicle,
    isError: isErrorRentalVehicle,
  } = useGetRentalVehicleQuery(
    {vehicleId: rentalOrder?.rentalVehicleId ?? ''},
    {
      skip: isNil(rentalOrder?.rentalVehicleId),
    }
  );

  const {
    data: vehicle,
    isLoading: isLoadingVehicle,
    isError: isErrorVehicle,
  } = useGetVehicleQuery(
    {vehicleId: rentalVehicle?.vehicleId ?? ''},
    {skip: isNil(rentalVehicle?.vehicleId)}
  );

  const levelIndicators = levelIndicatorsFor(vehicle?.fuelType);

  const [handleUpdateOdometer] = useUpdateOdometer(rentalVehicle?.vehicleId);
  const [submitType, setSubmitType] = useState<'rentAndGenerate' | 'saveChanges' | 'generate'>(
    'saveChanges'
  );
  const shouldRentVehicle = submitType === 'rentAndGenerate';
  const shouldGenerateContract = submitType === 'generate' || submitType === 'rentAndGenerate';

  const [defaultValues, areDefaultValuesLoading] = useGetRentVehicleFormDefaultValues(
    rentalOrder,
    rentalVehicle
  );

  const isLoading =
    isLoadingRentalOrder || isLoadingRentalVehicle || areDefaultValuesLoading || isLoadingVehicle;
  const isError = isErrorRentalOrder || isErrorRentalVehicle || isErrorVehicle;

  const updateNote = (note: string | Nullish, context: RentalOrderNodeContext) =>
    addNote({
      context,
      recordId: rentalOrderId,
      resourceId: EntityResourceIds.rentalOrder,
      updateNoteRequestBody: {
        text: note ?? '',
      },
    })
      .unwrap()
      .catch(handleApiError);

  const handleSubmit: FormSubmitHandler<RentVehicleFormValues> = async (
    data,
    setErrors,
    reset,
    blockerContext
  ) => {
    const changeOrderRentVehicleRequestBody = getRentVehicleRequestBody(
      data,
      vatRates,
      levelIndicators
    );

    await updateRentalOrderVehicle({
      rentalOrderId,
      changeOrderRentVehicleRequestBody,
    })
      .unwrap()
      .then(async () => {
        reset(data);
        updateNote(data.conditionNote, RentalOrderNodeContext.rentVehicleConditionNote);
        updateNote(data.rentalOrderNote, RentalOrderNodeContext.rentVehicleNote);

        if (shouldRentVehicle) {
          await rentVehicle({
            rentalOrderId,
          }).unwrap();
        }

        if (shouldGenerateContract) {
          await generateRentalContract({
            rentalOrderId,
            templateId: data.templateId,
          }).unwrap();
        }
      })
      .then(() => {
        const isNewEntity = shouldRentVehicle;

        const recordTitle = `${rentalOrder?.number} - ${vehicle?.title}`;

        if (isNewEntity || (vehicleMileage?.mileage ?? 0) < data.actualMileage) {
          handleUpdateOdometer({
            recordId: rentalOrderId,
            milage: data.actualMileage,
            action: 'car-rental-rent',
            recordTitle,
          });
        }
      })
      .catch((error: ApiError) =>
        handleApiError(error, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <VStack width="100%" spacing={4}>
      <Show when={isVehicleNotRentable}>
        <NotRentableVehicleAlert
          rentedOrder={rentingState?.rentedOrder}
          data-testid={testIds.rentalOrder.rentVehicle('notRentableVehicleAlert')}
        />
      </Show>

      <Card title={i18n.t('page.rentalOrder.labels.rentVehicle')}>
        <DataStatus isLoading={isLoading} isError={isError} minHeight={100}>
          <Form<RentVehicleFormValues>
            defaultValues={defaultValues}
            experimentalZodSchema={rentVehicleFormSchema}
            onSubmit={handleSubmit}
            shouldWatchForUnsavedChanges
          >
            {(control, formApi) => (
              <VStack spacing={4}>
                <AuthorizedPersons
                  control={control}
                  formApi={formApi}
                  data-testid={testIds.rentalOrder.rentVehicle('RentAndReturnAddress')}
                />
                <RentAndReturnAddress
                  control={control}
                  formApi={formApi}
                  data-testid={testIds.rentalOrder.rentVehicle('RentAndReturnAddress')}
                />
                <ConditionWhenRenting
                  control={control}
                  formApi={formApi}
                  levelIndicators={levelIndicators}
                  data-testid={testIds.rentalOrder.rentVehicle('conditionWhenReturning')}
                />

                <Separator spacing={0} />

                <Pricing
                  control={control}
                  formApi={formApi}
                  rentalVehicle={rentalVehicle}
                  rentalOrder={rentalOrder}
                  data-testid={testIds.rentalOrder.rentVehicle('pricing')}
                />

                <Separator spacing={0} />

                <RentalNote
                  control={control}
                  formApi={formApi}
                  data-testid={testIds.rentalOrder.rentVehicle('rentalNote')}
                />

                <ButtonGroup align="right">
                  <FormButton
                    control={control}
                    variant="secondary"
                    type="submit"
                    onClick={() => setSubmitType('saveChanges')}
                    title={i18n.t('general.actions.saveChanges')}
                  />

                  <Tooltip
                    isDisabled={!isVehicleNotRentable}
                    description={i18n.t('page.rentalOrder.labels.notRentableVehicleGeneral')}
                  >
                    <FormButton
                      control={control}
                      isDisabled={isVehicleNotRentable}
                      onClick={() => {
                        openDialog(
                          <SelectTemplateAndSubmit
                            control={control}
                            data-testid={testIds.rentalOrder.rentVehicle('selectTemplateAndSubmit')}
                            onSubmit={() =>
                              setSubmitType(
                                rentalOrder?.state === 'reserved' ? 'rentAndGenerate' : 'generate'
                              )
                            }
                          />,
                          {title: i18n.t('page.rentalOrder.labels.contractTemplate'), size: 'small'}
                        );
                      }}
                      title={
                        rentalOrder?.state === 'reserved'
                          ? i18n.t('page.rentalOrder.labels.rentAndGenerate')
                          : i18n.t('page.rentalOrder.labels.generateRentalContract')
                      }
                    />
                  </Tooltip>
                </ButtonGroup>
              </VStack>
            )}
          </Form>
        </DataStatus>
      </Card>
    </VStack>
  );
}
